/**
 * "Revelar por passo" — a ação que marca e desmarca, na barra do elemento selecionado.
 *
 * ═══ Por que ela não edita arquivo nenhum do upstream ══════════════════════════════════════
 *
 * `resolveToolbarActions` (`widgets/toolbar/src/utils.ts:227`) combina, para todo elemento de
 * superfície, os módulos `affine:surface:*` **e `custom:affine:surface:*`**. O segundo é o
 * slot deixado para quem estende — registrar ali é aditivo por desenho, e é o mesmo caminho
 * que `frame-toolbar.ts:207-218` usa para o frame.
 *
 * Ou seja: marcar um elemento custa **zero** arquivo do upstream editado. Os dois patches
 * obrigatórios (`frame-model.ts` e `edgeless-clipboard-config.ts`) continuam sendo os únicos
 * que existem por causa do modelo — não por causa da UI.
 *
 * ═══ Quando ela aparece ════════════════════════════════════════════════════════════════════
 *
 * Só quando há **exatamente um** elemento selecionado e ele é **filho direto de um frame**.
 *
 * - Vários selecionados: marcar em lote parece conveniente e produz uma ordem que ninguém
 *   escolheu — a ordem é o produto aqui, não um detalhe.
 * - Fora de frame: não há onde guardar o passo. Mostrar a ação desabilitada ensinaria que a
 *   funcionalidade não funciona; não mostrar ensina que ela é do quadro.
 * - Elemento dentro de um GRUPO: o pai é o grupo, não o frame (`frameDoElemento`), então a
 *   ação não aparece — e isso é coerente com o produto, porque quem agrupou pediu que aquilo
 *   se comportasse como uma coisa só. Marcar o GRUPO funciona e revela tudo dentro.
 */

import { EdgelessCRUDIdentifier } from '@blocksuite/affine-block-surface';
import {
  ActionPlacement,
  type ToolbarContext,
  type ToolbarModuleConfig,
  ToolbarModuleExtension,
} from '@blocksuite/affine-shared/services';
import { BlockFlavourIdentifier } from '@blocksuite/std';
import { GfxControllerIdentifier, type GfxModel } from '@blocksuite/std/gfx';
import type { ExtensionType } from '@blocksuite/store';

import { badgeDePassos } from './badge';
import {
  desmarcarElemento,
  elementoEstaMarcado,
  frameDoElemento,
  marcarElemento,
} from './marcacao';

/** O único selecionado, se houver exatamente um. */
function unicoSelecionado(ctx: ToolbarContext): GfxModel | null {
  const gfx = ctx.std.get(GfxControllerIdentifier);
  const selecionados = gfx.selection.selectedElements;
  return selecionados.length === 1 ? (selecionados[0] as GfxModel) : null;
}

/** O par (elemento, frame) quando os dois existem — a pré-condição de tudo aqui. */
function alvo(ctx: ToolbarContext) {
  const elemento = unicoSelecionado(ctx);
  if (!elemento) return null;
  const frame = frameDoElemento(elemento);
  return frame ? { elemento, frame } : null;
}

const configuracao = {
  actions: [
    {
      id: 'z.tuninho-revelar-por-passo',
      placement: ActionPlacement.More,
      when: (ctx: ToolbarContext) => !!alvo(ctx),
      /*
       * ⚠️ **`generate`, e NÃO `label` com função.**
       *
       * `ToolbarAction.label` é `string` (`toolbar-service/action.ts:22`) — só `when`, `active`
       * e `disabled` aceitam função. Passar uma função ali **não dá erro de tipo em runtime e
       * não avisa nada**: o Lit interpola o valor, e o menu passa a mostrar o **código-fonte da
       * função** como rótulo do item.
       *
       * Medido no `dev` em 2026-08-29, e é o formato exato do achado: a ação aparecia no ⋮ com
       * o texto `e=>{let t=yo(e);return t&&yt(t.frame,t.e…`. O registro estava certo, o slot
       * estava certo, a ação funcionava ao ser clicada — e o rótulo era ilegível.
       *
       * `ToolbarActionGenerator` (`action.ts:34`) é o mecanismo para ação cujo conteúdo depende
       * do estado: devolve a ação já resolvida, ou `null` para não aparecer.
       */
      generate: (ctx: ToolbarContext) => {
        const a = alvo(ctx);
        if (!a) return null;

        const marcado = elementoEstaMarcado(a.frame, a.elemento.id);
        return {
          label: marcado ? 'Não revelar por passo' : 'Revelar por passo',
          run: () => {
            const crud = ctx.std.get(EdgelessCRUDIdentifier);
            if (marcado) {
              desmarcarElemento(crud, a.frame, a.elemento.id);
            } else {
              marcarElemento(crud, a.frame, a.elemento.id);
            }
            ctx.store.captureSync();

            // O badge é desenhado num overlay do canvas, e `presentationSteps` vive no FRAME —
            // então mexer nele não dispara o `elementUpdated` do elemento que ganhou o número.
            // Sem este pedido explícito, o número só apareceria no próximo redesenho por outro
            // motivo (um pan, um clique). Nada falharia; o badge é que chegaria atrasado.
            badgeDePassos(ctx.std.get(GfxControllerIdentifier))?.refresh();
          },
        };
      },
    },
  ],
} as const satisfies ToolbarModuleConfig;

export const acaoDeMarcarPasso: ExtensionType = ToolbarModuleExtension({
  id: BlockFlavourIdentifier('custom:affine:surface:*'),
  config: configuracao,
});
